import { useState, useEffect, useRef, type CSSProperties } from 'react';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'motion/react';
import { Avatar, Tooltip, Flex, Text, IconButton, floatingEntrance, Icon } from '@nula/core-ui';
import { useAwareness } from './awareness-provider';
import styles from './presence-indicator.module.css';
import { useAuthStore } from '@/stores/use-auth-store';
import { collabColorFromId } from '@/utils/collab-color';
import { create } from 'zustand';

// ── Cursor visibility store (shared with CollaborativeEditor) ────────────

interface CursorVisibilityState {
  /** Set of user IDs whose cursors are hidden */
  hiddenIds: Set<string>;
  /** Hide all remote cursors */
  allHidden: boolean;
  toggleUser: (userId: string) => void;
  toggleAll: () => void;
}

export const useCursorVisibility = create<CursorVisibilityState>((set, _get) => ({
  hiddenIds: new Set(),
  allHidden: false,
  toggleUser: (userId) => set((s) => {
    const next = new Set(s.hiddenIds);
    if (next.has(userId)) next.delete(userId); else next.add(userId);
    return { hiddenIds: next };
  }),
  toggleAll: () => set((s) => ({ allHidden: !s.allHidden })),
}));

interface PresenceIndicatorProps {
  entityId: string;
  /** Show the current user's avatar too (for Actionbar). Default: true */
  showSelf?: boolean;
  /**
   * Scope presence to a single sub-view of a multi-surface entity (e.g. one
   * derived tab): only peers co-present on the SAME `viewId` are shown. Omit
   * for a single-surface entity (unscoped — every present peer, legacy
   * behavior). The host must also publish the view via `usePublishAwarenessView`.
   */
  viewId?: string;
}

export function PresenceIndicator({ entityId, showSelf = true, viewId }: PresenceIndicatorProps) {
  const { remoteUsers } = useAwareness(entityId, viewId);
  const currentUser = useAuthStore((s) => s.user);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!popoverOpen) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', handler, true), 50);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', handler, true); };
  }, [popoverOpen]);

  const { hiddenIds, allHidden, toggleUser, toggleAll } = useCursorVisibility();

  const hasRemote = remoteUsers.length > 0;
  const hasSelf = showSelf && currentUser;

  if (!hasRemote && !hasSelf) return null;

  const selfColor = collabColorFromId(currentUser?.sub ?? 'anonymous');
  const allUsers = [
    ...(hasSelf ? [{ id: 'self', name: `${currentUser.name} (You)`, avatar: currentUser.picture, color: selfColor, isSelf: true, isIdle: false }] : []),
    ...remoteUsers.map((u) => ({ id: u.id, name: u.name, avatar: u.avatar, color: u.color, isSelf: false, isIdle: u.isIdle })),
  ];

  return (
    <Flex
      className={styles.container}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setPopoverOpen(false);
        }
      }}
    >
      {/* Avatar trigger — transparent button wrapping Avatar.Group */}
      <IconButton
        type="button"
        aria-label={`Show collaborators (${allUsers.length} online)`}
        aria-expanded={popoverOpen}
        onClick={() => setPopoverOpen(!popoverOpen)}
        className={styles.avatarTrigger}
      >
        <Avatar.Group maxCount={4} size="sm">
          {hasSelf && (
            <Avatar
              size="sm"
              src={currentUser.picture}
              className={styles.presenceAvatar}
              style={{ '--presence-avatar-bg': selfColor } as CSSProperties}
            >
              {(currentUser.name ?? 'U').charAt(0).toUpperCase()}
            </Avatar>
          )}
          {remoteUsers.map((user) => (
            <Avatar
              key={user.id}
              size="sm"
              src={user.avatar}
              className={clsx(styles.presenceAvatar, user.isIdle && styles.idle)}
              style={{ '--presence-avatar-bg': user.color } as CSSProperties}
            >
              {user.name[0]}
            </Avatar>
          ))}
        </Avatar.Group>
      </IconButton>

      {/* Collaborators popover */}
      <AnimatePresence>
        {popoverOpen && (
          <motion.div
            ref={popoverRef}
            {...floatingEntrance.dropdown}
            className={styles.popover}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <Flex
              align="center"
              justify="space-between"
              className={styles.popoverHeader}
            >
              <Flex align="center" gap="sm">
                <Icon name="group" size={14} weight={300} className={styles.groupIcon} />
                <Text className={styles.popoverTitle}>
                  {(() => {
                    const activeCount = allUsers.filter((u) => !u.isIdle).length;
                    const totalCount = allUsers.length;
                    return activeCount === totalCount
                      ? `Active Here (${totalCount})`
                      : `Active Here (${activeCount} / ${totalCount})`;
                  })()}
                </Text>
              </Flex>
              {hasRemote && (
                <Tooltip title={allHidden ? 'Show all cursors' : 'Hide all cursors'}>
                  <IconButton
                    size="sm"
                    aria-label={allHidden ? 'Show all cursors' : 'Hide all cursors'}
                    onClick={toggleAll}
                    className={styles.visibilityBtn}
                    style={{ '--btn-opacity': allHidden ? 0.4 : 0.7 } as CSSProperties}
                  >
                    <Icon name={allHidden ? 'visibility_off' : 'visibility'} size={14} weight={300} />
                  </IconButton>
                </Tooltip>
              )}
            </Flex>

            {/* User list */}
            <Flex vertical className={styles.userList}>
              {allUsers.map((user) => {
                const isHidden = !user.isSelf && (allHidden || hiddenIds.has(user.id));
                return (
                  <Flex
                    key={user.id}
                    align="center"
                    gap="md"
                    className={styles.userRow}
                    style={{ '--row-opacity': isHidden ? 0.5 : 1 } as CSSProperties}
                  >
                    <Avatar
                      size="sm"
                      src={user.avatar}
                      className={clsx(styles.presenceAvatar, styles.listAvatar, user.isIdle && styles.idle)}
                      style={{ '--presence-avatar-bg': user.color } as CSSProperties}
                    >
                      {user.name[0]}
                    </Avatar>
                    <Text className={styles.userName}>
                      {user.name}
                    </Text>
                    {user.isSelf ? (
                      <Flex className={styles.selfIndicator} />
                    ) : user.isIdle ? (
                      <Tooltip title="Idle">
                        <Icon name="bedtime" size={14} weight={300} className={styles.idleIcon} />
                      </Tooltip>
                    ) : (
                      <Tooltip title={isHidden ? 'Show cursor' : 'Hide cursor'}>
                        <IconButton
                          size="sm"
                          aria-label={isHidden ? `Show ${user.name}'s cursor` : `Hide ${user.name}'s cursor`}
                          onClick={() => toggleUser(user.id)}
                          className={styles.visibilityBtn}
                          style={{ '--btn-opacity': isHidden ? 0.4 : 0.7 } as CSSProperties}
                        >
                          <Icon name={isHidden ? 'visibility_off' : 'visibility'} size={14} weight={300} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Flex>
                );
              })}
            </Flex>
          </motion.div>
        )}
      </AnimatePresence>
    </Flex>
  );
}
